/**
 * Tableau générique triable / filtrable par colonne (façon Excel) /
 * redimensionnable / exportable (Excel + PDF). Réutilisé par toutes les
 * pages de tableaux (Findings, Actions, CCV, PCED...).
 *
 * Basé sur TanStack Table (headless) : la mise en page (Shadcn Table) reste
 * inchangée, seule la logique de tri/filtre/pagination/redimensionnement est
 * désormais gérée par la librairie.
 */
import { useEffect, useMemo, useState } from "react";
import {
  flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel,
  getSortedRowModel, useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, FileSpreadsheet, FileText, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { exportToExcel, exportToPDF } from "@/lib/exportUtils";
import { toast } from "@/hooks/use-toast";
import { TABLE } from "@/constants/testIds";

const PAGE_SIZE = 10;
const MAX_SELECT_OPTIONS = 8;

// Valeur "plate" (string) utilisée pour le tri/filtre d'une colonne : les
// booléens (late, cis_france...) sont convertis en "Oui"/"Non" pour être
// filtrables comme du texte, indépendamment du rendu visuel (Badge...).
function flatValue(raw) {
  if (typeof raw === "boolean") return raw ? "Oui" : "Non";
  return raw ?? "";
}

export default function DataTable({ testId, title, columns, data = [], renderCell, defaultSort }) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState(defaultSort || []);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });

  // Options "Excel-style" pour chaque colonne filtrable : liste des valeurs
  // uniques présentes dans les données (utilisée pour afficher un <select>
  // au lieu d'un champ texte quand il y a peu de valeurs distinctes).
  const uniqueOptionsByKey = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      if (col.sortable === false) return;
      const values = new Set();
      data.forEach((row) => {
        const v = flatValue(row[col.key]);
        if (v !== "") values.add(String(v));
      });
      map[col.key] = Array.from(values).sort();
    });
    return map;
  }, [columns, data]);

  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        id: col.key,
        header: col.label,
        accessorFn: (row) => flatValue(row[col.key]),
        enableSorting: col.sortable !== false,
        enableColumnFilter: col.sortable !== false,
        size: col.width ?? 160,
        minSize: 70,
        maxSize: 480,
        cell: (info) => {
          const row = info.row.original;
          const rendered = renderCell ? renderCell(col, row) : undefined;
          return rendered !== undefined ? rendered : String(row[col.key] ?? "-");
        },
      })),
    [columns, renderCell]
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { globalFilter, sorting, columnFilters, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const term = String(filterValue).toLowerCase();
      return columns.some((col) => String(flatValue(row.original[col.key])).toLowerCase().includes(term));
    },
  });

  const exportRows = table.getFilteredRowModel().rows.map((r) => r.original);
  const activeFilterCount = columnFilters.length + (globalFilter ? 1 : 0);

  // Filet de sécurité pagination : si un filtre global (Site, Personne...)
  // réduit fortement le volume de données pendant que l'utilisateur est sur
  // une page avancée (ex: page 30 d'une liste de 2645 lignes), l'index de
  // page peut se retrouver hors des limites du nouveau jeu de données
  // filtré (ex: 1 seule page pour Dunkerque) - TanStack Table affiche alors
  // un tableau vide au lieu de re-basculer automatiquement sur une page
  // valide. On ramène systématiquement l'index sur la dernière page valide.
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pagination.pageIndex > 0 && pagination.pageIndex >= pageCount) {
      setPagination((p) => ({ ...p, pageIndex: Math.max(0, pageCount - 1) }));
    }
  }, [pageCount, pagination.pageIndex]);

  const resetAllFilters = () => {
    setGlobalFilter("");
    setColumnFilters([]);
  };

  const handleExportExcel = () => {
    exportToExcel(exportRows, columns, title.toLowerCase().replace(/\s+/g, "_"));
    toast({ title: "Export Excel généré", description: `${exportRows.length} ligne(s) exportée(s).` });
  };

  const handleExportPDF = () => {
    exportToPDF(exportRows, columns, title.toLowerCase().replace(/\s+/g, "_"), title);
    toast({ title: "Export PDF généré", description: `${exportRows.length} ligne(s) exportée(s).` });
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              data-testid={TABLE.searchInput}
              placeholder="Rechercher..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="h-9 pl-8 text-sm"
            />
          </div>
          {activeFilterCount > 0 && (
            <Button
              data-testid={TABLE.resetFiltersButton}
              variant="ghost"
              size="sm"
              onClick={resetAllFilters}
              className="h-9 gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" /> Réinitialiser ({activeFilterCount})
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid={TABLE.exportExcelButton} variant="outline" size="sm" onClick={handleExportExcel} className="gap-1.5 text-xs">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </Button>
          <Button data-testid={TABLE.exportPdfButton} variant="outline" size="sm" onClick={handleExportPDF} className="gap-1.5 text-xs">
            <FileText className="h-3.5 w-3.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <Table style={{ width: table.getTotalSize(), tableLayout: "fixed" }}>
          <TableHeader className="bg-slate-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: header.getSize(), position: "relative" }}
                    className="select-none"
                  >
                    <span
                      data-testid={`${TABLE.sortHeader}-${header.column.id}`}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`flex items-center gap-1 ${header.column.getCanSort() ? "cursor-pointer hover:text-slate-700" : ""}`}
                    >
                      {header.column.columnDef.header}
                      {header.column.getCanSort() && (
                        header.column.getIsSorted() === "asc" ? <ArrowUp className="h-3 w-3" /> :
                        header.column.getIsSorted() === "desc" ? <ArrowDown className="h-3 w-3" /> :
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </span>
                    {header.column.getCanResize() && (
                      <div
                        data-testid={`${TABLE.resizeHandle}-${header.column.id}`}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-[#0B5394]/30 ${header.column.getIsResizing() ? "bg-[#0B5394]/50" : ""}`}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
            {/* Ligne de filtres "Excel-style" : un input/select par colonne filtrable */}
            <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
              {table.getHeaderGroups()[0]?.headers.map((header) => {
                const col = header.column;
                if (!col.getCanFilter()) {
                  return <TableHead key={`filter-${header.id}`} style={{ width: header.getSize() }} />;
                }
                const options = uniqueOptionsByKey[col.id] || [];
                const useSelect = options.length > 0 && options.length <= MAX_SELECT_OPTIONS;
                return (
                  <TableHead key={`filter-${header.id}`} style={{ width: header.getSize() }} className="py-1.5">
                    {useSelect ? (
                      <Select
                        value={col.getFilterValue() ?? "__all__"}
                        onValueChange={(v) => col.setFilterValue(v === "__all__" ? undefined : v)}
                      >
                        <SelectTrigger
                          data-testid={`${TABLE.columnFilter}-${col.id}`}
                          className="h-7 border-slate-200 bg-white px-2 text-[11px]"
                        >
                          <SelectValue placeholder="Tous" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">Tous</SelectItem>
                          {options.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        data-testid={`${TABLE.columnFilter}-${col.id}`}
                        value={col.getFilterValue() ?? ""}
                        onChange={(e) => col.setFilterValue(e.target.value || undefined)}
                        placeholder="Filtrer..."
                        className="h-7 border-slate-200 bg-white px-2 text-[11px]"
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-8 text-center text-sm text-slate-400">
                  Aucune donnée trouvée.
                </TableCell>
              </TableRow>
            )}
            {table.getRowModel().rows.map((row, idx) => (
              <TableRow key={row.id} data-testid={`${TABLE.row}-${idx}`} className="hover:bg-slate-50">
                {row.getVisibleCells().map((cell) => {
                  const wrap = columns.find((c) => c.key === cell.column.id)?.wrap;
                  return (
                    <TableCell
                      key={cell.id}
                      style={{ width: cell.column.getSize() }}
                      className={wrap ? "whitespace-normal break-words text-sm" : "overflow-hidden truncate text-sm"}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{table.getFilteredRowModel().rows.length} résultat(s)</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()} className="h-7 text-xs"
          >
            Précédent
          </Button>
          <span>
            Page {pagination.pageIndex + 1} / {Math.max(1, table.getPageCount())}
          </span>
          <Button
            variant="outline" size="sm" disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()} className="h-7 text-xs"
          >
            Suivant
          </Button>
        </div>
      </div>
    </div>
  );
}
